import { lazy } from 'react';
import { imageDataToDataUrl } from '.';
import { Pantone, getClosestPantone, getPantoneColours, pantoneToPixel } from './pantone';

export function isCloseToBlack(hexCode: string, threshold = 128): boolean {
	// Remove the '#' symbol if present
	const cleanHexCode = hexCode.replace('#', '');
	// Convert the hex code to RGB values
	const red = parseInt(cleanHexCode.substring(0, 2), 16);
	const green = parseInt(cleanHexCode.substring(2, 4), 16);
	const blue = parseInt(cleanHexCode.substring(4, 6), 16);
	// Calculate color difference with black
	const colorDifference = Math.sqrt(Math.pow(red, 2) + Math.pow(green, 2) + Math.pow(blue, 2));

	// Compare the brightness level with the threshold
	return colorDifference <= threshold;
}

export function isCloseToWhite(hexCode: string, threshold = 0.5): boolean {
	// Remove the '#' symbol if present
	const cleanHexCode = hexCode.replace('#', '');
	// Convert the hex code to RGB values
	const r = parseInt(cleanHexCode.substring(0, 2), 16);
	const g = parseInt(cleanHexCode.substring(2, 4), 16);
	const b = parseInt(cleanHexCode.substring(4, 6), 16);

	// Calculate color difference with white
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
	// const colorDifference = Math.sqrt(Math.pow(255 - red, 2) + Math.pow(255 - green, 2) + Math.pow(255 - blue, 2));
	// Compare the brightness level with the threshold
	return luminance > threshold;
}

export type RecolourList = Array<{ oldColour: number[]; newColour: number[] }>;

export type RGB = {
	r: number;
	g: number;
	b: number;
};

const isRGB = (colour: number[] | RGB): colour is RGB => {
	return (colour as RGB).r !== undefined;
};

export const getMatchingRecolour = (recolourList: RecolourList, oldColour: number[] | RGB): number[] | undefined => {
	const recolour = recolourList.find(entry =>
		entry.oldColour.every((value, index) => {
			if (isRGB(oldColour)) {
				const { r, g, b } = oldColour;
				return value === [r, g, b][index];
			}
			return value === oldColour[index];
		}),
	);
	return recolour?.newColour;
};

export const getClosestMatchingColour = (colourList: number[][], oldColour: number[] | RGB) => {
	let closestColour: number[] = [];
	let closestDistance = Infinity;

	let oldColourArray: number[];
	if (isRGB(oldColour)) {
		oldColourArray = [oldColour.r, oldColour.g, oldColour.b];
	} else {
		oldColourArray = oldColour;
	}

	for (const newColour of colourList) {
		const distance = Math.sqrt(
			Math.pow(newColour[0] - oldColourArray[0], 2) +
				Math.pow(newColour[1] - oldColourArray[1], 2) +
				Math.pow(newColour[2] - oldColourArray[2], 2),
		);
		if (distance < closestDistance) {
			closestColour = newColour;
			closestDistance = distance;
		}
	}

	return closestColour;
};

interface UpdateImageColoursOptions {
	imageData: ImageData;
	palette?: Pantone[];
	maxColours?: number;
	removals?: string[];
	replacements?: Record<string, Pantone>;
}

export async function updateImageColours({ palette, imageData, maxColours = 6, removals, replacements }: UpdateImageColoursOptions) {
	//Get the colour list and remove any colours that are to be removed
	const colourList = (palette || (await getColourPalette(imageData, maxColours))).filter(pantone => !removals?.includes(pantone.name));

	const newImageData = new ImageData(imageData.width, imageData.height);

	//Update the imageData with the new palette
	for (let i = 0; i < imageData.data.length; i += 4) {
		let pixel = [imageData.data[i], imageData.data[i + 1], imageData.data[i + 2]];
		const pantone = getClosestPantone(pixel, colourList);

		if (!!replacements?.[pantone.name]) {
			pixel = pantoneToPixel(replacements[pantone.name]);
		} else {
			pixel = pantoneToPixel(pantone);
		}

		newImageData.data[i] = pixel[0];
		newImageData.data[i + 1] = pixel[1];
		newImageData.data[i + 2] = pixel[2];
		newImageData.data[i + 3] = imageData.data[i + 3];
	}

	return {
		imageData: newImageData,
		palette: colourList,
	};
}

export async function getColourPalette(imageData: ImageData, maxColours = 6) {
	// @ts-ignore
	const ColorThief = (await import('colorthief/dist/color-thief.umd.js')).default;
	var colorThief = new ColorThief();

	return new Promise<Pantone[]>(async (resolve, reject) => {
		const pantones = await getPantoneColours();
		const imgElement = document.createElement('img');
		imgElement.src = imageDataToDataUrl(imageData);
		imgElement.onload = () => {
			const palette = colorThief.getPalette(imgElement, maxColours);
			resolve(palette.map((p: number[]) => getClosestPantone(p, pantones)));
		};
	});
}

export function hexToRGBAString(hex?: string | null, alpha = 1) {
	if (!hex) return 'rgba(0, 0, 0, 0)';
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function hexToPixel(hex: string) {
	const r = parseInt(hex.substring(1, 3), 16);
	const g = parseInt(hex.substring(3, 5), 16);
	const b = parseInt(hex.substring(5, 7), 16);
	return [r, g, b];
}

export function rgbStringToPixel(rgb: string) {
	//Check if it's rgba or rgb
	const rgba = rgb.replace('rgba(', '').replace('rgb(', '').replace(')', '').split(',');
	const r = parseInt(rgba[0]);
	const g = parseInt(rgba[1]);
	const b = parseInt(rgba[2]);
	return [r, g, b];
}

export function hexOrRGBStringToPixel(colour: string) {
	if (colour.startsWith('#')) {
		return hexToPixel(colour);
	}
	return rgbStringToPixel(colour);
}

export const getLuminance = (hex: string): number => {
	const [r, g, b] = hexToPixel(hex);
	const a = [r, g, b].map(v => {
		v /= 255;
		return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
	});
	return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

export const getContrastRatio = (l1: number, l2: number): number => {
	return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

export const getPreferredContrastColor = (bgColor: string, colourOne: string, colourTwo: string) => {
	const bgLuminance = getLuminance(bgColor);
	const luminance1 = getLuminance(colourOne);
	const luminance2 = getLuminance(colourTwo);

	const contrast1 = getContrastRatio(bgLuminance, luminance1);
	const contrast2 = getContrastRatio(bgLuminance, luminance2);

	return contrast1 > contrast2 ? colourOne : colourTwo;
};
